import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './QuestionsTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const DATASET_TYPE_IS_CORRECT = 'is-correct';
const DATASET_TYPE_RESULT = 'result';

const DATASET_TYPES = [
  { id: DATASET_TYPE_IS_CORRECT, label: 'is-correct (result 0 или 1)' },
  { id: DATASET_TYPE_RESULT, label: 'result (result от 1 до 10)' },
];

function getDatasetTypeFromName(name) {
  if (!name || typeof name !== 'string') return DATASET_TYPE_IS_CORRECT;
  return name.trim().toLowerCase().startsWith('result-') ? DATASET_TYPE_RESULT : DATASET_TYPE_IS_CORRECT;
}

function getEmptyLine(datasetType) {
  return {
    message: { question: '', correct_answer: '', user_message: '' },
    result: datasetType === DATASET_TYPE_RESULT ? 1 : 0,
  };
}

function formatDate(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function truncate(str, len = 80) {
  if (typeof str !== 'string') return '—';
  return str.length <= len ? str : str.slice(0, len) + '…';
}

function normalizeLine(obj, datasetType) {
  if (!obj || typeof obj !== 'object') return null;
  const msg = obj.message;
  if (!msg || typeof msg !== 'object') return null;
  const q = typeof msg.question === 'string' ? msg.question : String(msg.question ?? '');
  const ca = typeof msg.correct_answer === 'string' ? msg.correct_answer : String(msg.correct_answer ?? '');
  const um = typeof msg.user_message === 'string' ? msg.user_message : String(msg.user_message ?? '');
  let r = obj.result;
  if (datasetType === DATASET_TYPE_RESULT) {
    r = parseInt(r, 10);
    if (Number.isNaN(r) || r < 1 || r > 10) return null;
  } else {
    if (r === true || r === 1 || r === '1') r = 1;
    else if (r === false || r === 0 || r === '0') r = 0;
    else return null;
  }
  return { message: { question: q, correct_answer: ca, user_message: um }, result: r };
}

function parseDatasetContent(text, datasetType) {
  const type = datasetType || DATASET_TYPE_IS_CORRECT;
  if (!text || typeof text !== 'string') return { lines: [], error: 'Нет содержимого' };
  const trimmed = text.trim();
  if (!trimmed) return { lines: [], error: null };
  const rawLines = trimmed.split('\n').map(s => s.trim()).filter(Boolean);
  const lines = [];
  for (let i = 0; i < rawLines.length; i++) {
    try {
      const obj = JSON.parse(rawLines[i]);
      const norm = normalizeLine(obj, type);
      if (norm) lines.push(norm);
      else
        return {
          lines: [],
          error: `Строка ${i + 1}: неверная структура (message.question, correct_answer, user_message и result${type === DATASET_TYPE_RESULT ? ' 1–10' : ' 0/1'})`,
        };
    } catch (e) {
      return { lines: [], error: `Строка ${i + 1}: невалидный JSON` };
    }
  }
  return { lines, error: null };
}

function serializeLines(lines) {
  return lines.map(line => JSON.stringify(line)).join('\n');
}

function validateLine(line, datasetType) {
  if (!line || !line.message) return false;
  const { question, correct_answer, user_message } = line.message;
  if (typeof question !== 'string' || typeof correct_answer !== 'string' || typeof user_message !== 'string') return false;
  if (!question.trim() || !correct_answer.trim() || !user_message.trim()) return false;
  const r = line.result;
  if (datasetType === DATASET_TYPE_RESULT) return Number.isInteger(r) && r >= 1 && r <= 10;
  return r === 0 || r === 1;
}

function validateAllLines(lines, datasetType) {
  const type = datasetType || DATASET_TYPE_IS_CORRECT;
  return lines.length > 0 && lines.every(line => validateLine(line, type));
}

function DatasetsSubTab() {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createType, setCreateType] = useState(DATASET_TYPE_IS_CORRECT);
  const [createName, setCreateName] = useState('');
  const [createLines, setCreateLines] = useState([]);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [viewModal, setViewModal] = useState({
    open: false,
    id: null,
    name: '',
    type: DATASET_TYPE_IS_CORRECT,
    lines: [],
    linesOriginal: [],
    loading: false,
    saving: false,
    error: null,
    editingIndex: null,
    parseError: null,
  });
  const [createEditingIndex, setCreateEditingIndex] = useState(null);
  const [loadAnswersModalOpen, setLoadAnswersModalOpen] = useState(false);
  const [loadLimit, setLoadLimit] = useState(100);
  const [loadIsCorrectFilter, setLoadIsCorrectFilter] = useState([0, 1]);
  const [loadAnswersLoading, setLoadAnswersLoading] = useState(false);
  const [loadAnswersError, setLoadAnswersError] = useState(null);

  const fetchDatasets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/datasets`);
      setDatasets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError('Не удалось загрузить список датасетов: ' + (err.response?.data?.detail || err.message));
      setDatasets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDatasets();
  }, [fetchDatasets]);

  const canSaveCreate = createName.trim() && validateAllLines(createLines, createType) && createLines.length > 0 && !createSaving;
  const createFullName = createName.trim() ? `${createType}-${createName.trim()}` : '';

  const handleOpenCreate = () => {
    setCreateModalOpen(true);
    setCreateType(DATASET_TYPE_IS_CORRECT);
    setCreateName('');
    setCreateLines([]);
    setCreateError(null);
    setCreateEditingIndex(null);
  };

  const handleCloseCreate = () => {
    if (!createSaving) {
      setCreateModalOpen(false);
      setCreateType(DATASET_TYPE_IS_CORRECT);
      setCreateName('');
      setCreateLines([]);
      setCreateError(null);
      setCreateEditingIndex(null);
    }
  };

  const handleCreateAddLine = () => {
    const newLine = getEmptyLine(createType);
    setCreateLines(prev => [...prev, newLine]);
    setCreateEditingIndex(createLines.length);
  };

  const handleCreateUpdateLine = (index, field, value) => {
    setCreateLines(prev => {
      const next = prev.map((line, i) => {
        if (i !== index) return line;
        if (field === 'result') {
          if (createType === DATASET_TYPE_RESULT) {
            const n = parseInt(value, 10);
            return { ...line, result: Number.isNaN(n) ? 1 : Math.max(1, Math.min(10, n)) };
          }
          return { ...line, result: value === 1 || value === '1' ? 1 : 0 };
        }
        return { ...line, message: { ...line.message, [field]: value } };
      });
      return next;
    });
  };

  const handleCreateDeleteLine = (index) => {
    setCreateLines(prev => prev.filter((_, i) => i !== index));
    if (createEditingIndex === index) setCreateEditingIndex(null);
    else if (createEditingIndex !== null && createEditingIndex > index) setCreateEditingIndex(createEditingIndex - 1);
  };

  const handleSaveDataset = async () => {
    if (!canSaveCreate) return;
    setCreateSaving(true);
    setCreateError(null);
    try {
      const fullName = createFullName;
      const content = serializeLines(createLines);
      const blob = new Blob([content], { type: 'application/json; charset=utf-8' });
      const safeFileName = fullName.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
      const file = new File([blob], `${safeFileName}.jsonl`, { type: 'application/json' });
      const formData = new FormData();
      formData.append('name', fullName);
      formData.append('file', file);
      await axios.post(`${API_URL}/api/datasets`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleCloseCreate();
      fetchDatasets();
    } catch (err) {
      setCreateError(err.response?.data?.detail || err.message || 'Ошибка сохранения');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleOpenView = async (id, name) => {
    const datasetType = getDatasetTypeFromName(name);
    setViewModal({
      open: true,
      id,
      name,
      type: datasetType,
      lines: [],
      linesOriginal: [],
      loading: true,
      saving: false,
      error: null,
      editingIndex: null,
      parseError: null,
    });
    try {
      const res = await axios.get(`${API_URL}/api/datasets/${id}/content`, { responseType: 'text' });
      const text = typeof res.data === 'string' ? res.data : '';
      const { lines, error: parseError } = parseDatasetContent(text, datasetType);
      setViewModal(prev => ({
        ...prev,
        loading: false,
        lines: parseError ? [] : lines,
        linesOriginal: parseError ? [] : lines,
        parseError: parseError || null,
      }));
    } catch (err) {
      setViewModal(prev => ({
        ...prev,
        loading: false,
        error: 'Не удалось загрузить: ' + (err.response?.data?.detail || err.message),
      }));
    }
  };

  const handleCloseView = () => {
    if (!viewModal.saving) {
      setViewModal({
        open: false,
        id: null,
        name: '',
        type: DATASET_TYPE_IS_CORRECT,
        lines: [],
        linesOriginal: [],
        loading: false,
        saving: false,
        error: null,
        editingIndex: null,
        parseError: null,
      });
    }
  };

  const setViewLines = (updater) => {
    setViewModal(prev => ({ ...prev, lines: typeof updater === 'function' ? updater(prev.lines) : updater }));
  };

  const handleViewUpdateLine = (index, field, value) => {
    const datasetType = viewModal.type;
    setViewLines(prev => {
      const next = prev.map((line, i) => {
        if (i !== index) return line;
        if (field === 'result') {
          if (datasetType === DATASET_TYPE_RESULT) {
            const n = parseInt(value, 10);
            return { ...line, result: Number.isNaN(n) ? 1 : Math.max(1, Math.min(10, n)) };
          }
          return { ...line, result: value === 1 || value === '1' ? 1 : 0 };
        }
        return { ...line, message: { ...line.message, [field]: value } };
      });
      return next;
    });
  };

  const handleViewDeleteLine = (index) => {
    setViewLines(prev => prev.filter((_, i) => i !== index));
    setViewModal(prev => {
      if (prev.editingIndex === index) return { ...prev, editingIndex: null };
      if (prev.editingIndex != null && prev.editingIndex > index) return { ...prev, editingIndex: prev.editingIndex - 1 };
      return prev;
    });
  };

  const handleViewAddLine = () => {
    const newLine = getEmptyLine(viewModal.type);
    setViewLines(prev => [...prev, newLine]);
    setViewModal(prev => ({ ...prev, editingIndex: prev.lines.length }));
  };

  const handleSaveView = async () => {
    const { id, lines, linesOriginal, type: viewType } = viewModal;
    if (!validateAllLines(lines, viewType) || lines.length === 0 || JSON.stringify(lines) === JSON.stringify(linesOriginal) || !id) return;
    setViewModal(prev => ({ ...prev, saving: true, error: null }));
    try {
      const content = serializeLines(lines);
      await axios.put(`${API_URL}/api/datasets/${id}`, { content });
      const res = await axios.get(`${API_URL}/api/datasets/${id}/content`, { responseType: 'text' });
      const text = typeof res.data === 'string' ? res.data : '';
      const { lines: newLines, error: parseError } = parseDatasetContent(text, viewType);
      setViewModal(prev => ({
        ...prev,
        lines: parseError ? prev.lines : newLines,
        linesOriginal: parseError ? prev.linesOriginal : newLines,
        saving: false,
        editingIndex: null,
      }));
      fetchDatasets();
    } catch (err) {
      setViewModal(prev => ({
        ...prev,
        saving: false,
        error: err.response?.data?.detail || err.message || 'Ошибка сохранения',
      }));
    }
  };

  const viewDirty = viewModal.open && JSON.stringify(viewModal.lines) !== JSON.stringify(viewModal.linesOriginal);
  const canSaveView = viewModal.open && viewDirty && validateAllLines(viewModal.lines, viewModal.type) && viewModal.lines.length > 0 && !viewModal.loading && !viewModal.saving;
  const canDownloadView =
    viewModal.open &&
    !viewModal.loading &&
    !viewModal.parseError &&
    validateAllLines(viewModal.lines, viewModal.type) &&
    viewModal.lines.length > 0 &&
    !viewModal.saving;

  const handleDownloadView = async () => {
    if (!canDownloadView) return;
    const { id, lines, linesOriginal, name, type: viewType } = viewModal;
    const valid = validateAllLines(lines, viewType);
    if (!valid || lines.length === 0) return;
    const dirty = JSON.stringify(lines) !== JSON.stringify(linesOriginal);
    if (dirty) {
      setViewModal(prev => ({ ...prev, saving: true, error: null }));
      try {
        const content = serializeLines(lines);
        await axios.put(`${API_URL}/api/datasets/${id}`, { content });
        const res = await axios.get(`${API_URL}/api/datasets/${id}/content`, { responseType: 'text' });
        const text = typeof res.data === 'string' ? res.data : '';
        const { lines: newLines, error: parseError } = parseDatasetContent(text, viewType);
        setViewModal(prev => ({
          ...prev,
          lines: parseError ? prev.lines : newLines,
          linesOriginal: parseError ? prev.linesOriginal : newLines,
          saving: false,
        }));
      } catch (err) {
        setViewModal(prev => ({
          ...prev,
          saving: false,
          error: err.response?.data?.detail || err.message || 'Ошибка сохранения',
        }));
        return;
      }
    }
    const linesToDownload = dirty ? lines : viewModal.lines;
    const content = serializeLines(linesToDownload);
    const blob = new Blob([content], { type: 'application/json; charset=utf-8' });
    const safeName = (name || viewModal.name || 'dataset').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}.jsonl`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleOpenLoadAnswers = () => {
    setLoadLimit(100);
    setLoadIsCorrectFilter([0, 1]);
    setLoadAnswersError(null);
    setLoadAnswersModalOpen(true);
  };

  const handleCloseLoadAnswers = () => {
    if (!loadAnswersLoading) {
      setLoadAnswersModalOpen(false);
      setLoadAnswersError(null);
    }
  };

  const toggleLoadIsCorrect = (value) => {
    setLoadIsCorrectFilter(prev => {
      const next = prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value];
      return next.length ? next : prev;
    });
  };

  const canSubmitLoadAnswers =
    loadLimit >= 1 &&
    (viewModal.type !== DATASET_TYPE_IS_CORRECT || loadIsCorrectFilter.length > 0);

  const handleSubmitLoadAnswers = async () => {
    if (!canSubmitLoadAnswers || loadAnswersLoading) return;
    setLoadAnswersLoading(true);
    setLoadAnswersError(null);
    try {
      const body = {
        dataset_type: viewModal.type,
        limit: Number(loadLimit),
      };
      if (viewModal.type === DATASET_TYPE_IS_CORRECT) {
        body.is_correct_filter = loadIsCorrectFilter;
      }
      const existingUserMessages = (viewModal.lines || [])
        .map(line => line?.message?.user_message)
        .filter(um => um != null && String(um).trim() !== '');
      if (existingUserMessages.length > 0) {
        body.exclude_user_messages = existingUserMessages;
      }
      const res = await axios.post(`${API_URL}/api/datasets/load-user-answers`, body);
      const lines = res.data?.lines ?? [];
      const normalized = lines.map(item => ({
        message: {
          question: item.message?.question ?? '',
          correct_answer: item.message?.correct_answer ?? '',
          user_message: item.message?.user_message ?? '',
        },
        result: item.result ?? (viewModal.type === DATASET_TYPE_RESULT ? 1 : 0),
      }));
      setViewLines(prev => [...prev, ...normalized]);
      setLoadAnswersModalOpen(false);
    } catch (err) {
      setLoadAnswersError(err.response?.data?.detail || err.message || 'Ошибка подгрузки');
    } finally {
      setLoadAnswersLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Удалить датасет «${name || id}»? Файл будет удалён из хранилища.`)) return;
    if (deletingId) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API_URL}/api/datasets/${id}`);
      fetchDatasets();
    } catch (err) {
      alert('Ошибка удаления: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingId(null);
    }
  };

  const renderLineRow = (line, index, isEdit, onStartEdit, onCancelEdit, onUpdate, onDelete, onSaveRow, datasetType) => {
    const msg = line?.message || {};
    const q = msg.question ?? '';
    const ca = msg.correct_answer ?? '';
    const um = msg.user_message ?? '';
    const type = datasetType || DATASET_TYPE_IS_CORRECT;
    const result = type === DATASET_TYPE_RESULT
      ? (Number.isInteger(line?.result) && line.result >= 1 && line.result <= 10 ? line.result : 1)
      : (line?.result === 1 ? 1 : 0);

    const resultControl =
      type === DATASET_TYPE_RESULT ? (
        <select
          className="form-control dataset-view-input"
          value={result}
          onChange={e => onUpdate(index, 'result', e.target.value)}
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      ) : (
        <input
          type="number"
          min={0}
          max={1}
          className="form-control dataset-view-input dataset-view-result-number"
          value={result}
          onChange={e => {
            const v = e.target.value;
            const n = parseInt(v, 10);
            if (v === '' || Number.isNaN(n)) return;
            onUpdate(index, 'result', n === 1 ? 1 : 0);
          }}
          aria-label="Результат 0 или 1"
        />
      );

    if (isEdit) {
      return (
        <div key={index} className="dataset-line dataset-line-edit">
          <div className="dataset-line-edit-fields">
            <div className="form-group">
              <label>Вопрос (question)</label>
              <textarea
                className="form-control dataset-view-textarea"
                rows={3}
                value={q}
                onChange={e => onUpdate(index, 'question', e.target.value)}
                placeholder="Текст вопроса"
              />
            </div>
            <div className="form-group">
              <label>Правильный ответ (correct_answer)</label>
              <textarea
                className="form-control dataset-view-textarea"
                rows={3}
                value={ca}
                onChange={e => onUpdate(index, 'correct_answer', e.target.value)}
                placeholder="Правильный ответ"
              />
            </div>
            <div className="form-group">
              <label>Ответ пользователя (user_message)</label>
              <textarea
                className="form-control dataset-view-textarea"
                rows={3}
                value={um}
                onChange={e => onUpdate(index, 'user_message', e.target.value)}
                placeholder="Ответ пользователя"
              />
            </div>
            <div className="form-group">
              <label>Результат (result){type === DATASET_TYPE_RESULT ? ' — число от 1 до 10' : ' — введите 0 или 1'}</label>
              {resultControl}
            </div>
          </div>
          <div className="dataset-line-edit-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => onSaveRow(index)}>
              Готово
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onCancelEdit}>
              Отмена
            </button>
            <button type="button" className="btn btn-icon btn-icon-delete btn-sm" onClick={() => onDelete(index)} title="Удалить строку">
              ✕
            </button>
          </div>
        </div>
      );
    }

    return (
      <div key={index} className="dataset-line dataset-line-display">
        <div className="dataset-line-body">
          <div className="dataset-line-field">
            <span className="dataset-line-label">Вопрос:</span>
            <span className="dataset-line-value">{truncate(q, 120)}</span>
          </div>
          <div className="dataset-line-field">
            <span className="dataset-line-label">Правильный ответ:</span>
            <span className="dataset-line-value">{truncate(ca, 120)}</span>
          </div>
          <div className="dataset-line-field">
            <span className="dataset-line-label">Ответ пользователя:</span>
            <span className="dataset-line-value">{truncate(um, 120)}</span>
          </div>
          <div className="dataset-line-field">
            <span className="dataset-line-label">Результат:</span>
            <span className="dataset-line-value">{result}</span>
          </div>
        </div>
        <div className="dataset-line-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onStartEdit(index)}>
            Редактировать
          </button>
          <button type="button" className="btn btn-icon btn-icon-delete btn-sm" onClick={() => onDelete(index)} title="Удалить строку">
            ✕
          </button>
        </div>
      </div>
    );
  };

  const renderViewTableRow = (line, index, datasetType) => {
    const msg = line?.message || {};
    const q = msg.question ?? '';
    const ca = msg.correct_answer ?? '';
    const um = msg.user_message ?? '';
    const type = datasetType || DATASET_TYPE_IS_CORRECT;
    const result = type === DATASET_TYPE_RESULT
      ? (Number.isInteger(line?.result) && line.result >= 1 && line.result <= 10 ? line.result : 1)
      : (line?.result === 1 ? 1 : 0);
    const isEdit = viewModal.editingIndex === index;

    const resultEditControl =
      type === DATASET_TYPE_RESULT ? (
        <select
          className="form-control dataset-view-input"
          value={result}
          onChange={e => handleViewUpdateLine(index, 'result', e.target.value)}
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      ) : (
        <input
          type="number"
          min={0}
          max={1}
          className="form-control dataset-view-input dataset-view-result-number"
          value={result}
          onChange={e => {
            const v = e.target.value;
            const n = parseInt(v, 10);
            if (v === '' || Number.isNaN(n)) return;
            handleViewUpdateLine(index, 'result', n === 1 ? 1 : 0);
          }}
          aria-label="Результат 0 или 1"
        />
      );

    return (
      <tr key={index} className={isEdit ? 'dataset-view-row dataset-view-row-edit' : 'dataset-view-row'}>
        <td className="dataset-view-cell dataset-view-cell-question">
          {isEdit ? (
            <textarea
              className="form-control dataset-view-textarea dataset-view-cell-textarea"
              rows={3}
              value={q}
              onChange={e => handleViewUpdateLine(index, 'question', e.target.value)}
              placeholder="Вопрос"
            />
          ) : (
            <span className="dataset-view-cell-text" title={q}>{q || '—'}</span>
          )}
        </td>
        <td className="dataset-view-cell dataset-view-cell-correct">
          {isEdit ? (
            <textarea
              className="form-control dataset-view-textarea dataset-view-cell-textarea"
              rows={3}
              value={ca}
              onChange={e => handleViewUpdateLine(index, 'correct_answer', e.target.value)}
              placeholder="Правильный ответ"
            />
          ) : (
            <span className="dataset-view-cell-text" title={ca}>{ca || '—'}</span>
          )}
        </td>
        <td className="dataset-view-cell dataset-view-cell-user">
          {isEdit ? (
            <textarea
              className="form-control dataset-view-textarea dataset-view-cell-textarea"
              rows={3}
              value={um}
              onChange={e => handleViewUpdateLine(index, 'user_message', e.target.value)}
              placeholder="Ответ пользователя"
            />
          ) : (
            <span className="dataset-view-cell-text dataset-view-cell-text-user" title={um}>{um || '—'}</span>
          )}
        </td>
        <td className="dataset-view-cell dataset-view-cell-result">
          {isEdit ? resultEditControl : <span className="dataset-view-cell-text">{result}</span>}
        </td>
        <td className="dataset-view-cell dataset-view-cell-actions">
          {isEdit ? (
            <>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setViewModal(prev => ({ ...prev, editingIndex: null }))}>
                Готово
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setViewModal(prev => ({ ...prev, editingIndex: null }))}>
                Отмена
              </button>
              <button type="button" className="btn btn-icon btn-icon-delete btn-sm" onClick={() => handleViewDeleteLine(index)} title="Удалить строку">
                ✕
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setViewModal(prev => ({ ...prev, editingIndex: index }))}>
                Редактировать
              </button>
              <button type="button" className="btn btn-icon btn-icon-delete btn-sm" onClick={() => handleViewDeleteLine(index)} title="Удалить строку">
                ✕
              </button>
            </>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="datasets-sub-tab">
      <div className="datasets-toolbar">
        <button type="button" className="btn btn-primary" onClick={handleOpenCreate}>
          Создать датасет
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Загрузка списка датасетов…</div>
      ) : (
        <div className="datasets-list-wrap">
          {datasets.length === 0 ? (
            <p className="datasets-empty">Нет датасетов. Создайте первый с помощью кнопки «Создать датасет».</p>
          ) : (
            <table className="datasets-table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Дата создания</th>
                  <th>Дата изменения</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {datasets.map((d) => (
                  <tr key={d.id}>
                    <td className="datasets-table-name">{d.name}</td>
                    <td>{formatDate(d.created_at)}</td>
                    <td>{formatDate(d.updated_at)}</td>
                    <td className="datasets-table-actions">
                      <button
                        type="button"
                        className="btn-icon btn-icon-view"
                        title="Просмотр и редактирование"
                        onClick={() => handleOpenView(d.id, d.name)}
                      >
                        <span aria-hidden>👁</span>
                      </button>
                      <button
                        type="button"
                        className="btn-icon btn-icon-delete"
                        title="Удалить датасет"
                        onClick={() => handleDelete(d.id, d.name)}
                        disabled={deletingId === d.id}
                      >
                        <span aria-hidden>✕</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Fullscreen view/edit modal */}
      {viewModal.open && (
        <div className="modal-overlay datasets-view-overlay-fullscreen" onClick={handleCloseView}>
          <div className="modal-content datasets-view-modal-fullscreen" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Просмотр и редактирование: {viewModal.name}</h3>
              <button type="button" className="modal-close" onClick={handleCloseView} aria-label="Закрыть" disabled={viewModal.saving}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {viewModal.loading ? (
                <div className="loading">Загрузка содержимого из S3…</div>
              ) : viewModal.parseError ? (
                <div className="error-message">Файл не соответствует формату: {viewModal.parseError}</div>
              ) : (
                <>
                  <div className="datasets-view-toolbar">
                    <span className="datasets-view-type-hint">
                      Тип: {viewModal.type === DATASET_TYPE_RESULT ? 'result (1–10)' : 'is-correct (0 или 1)'}
                    </span>
                    <div className="datasets-view-toolbar-actions">
                      <button type="button" className="btn btn-primary" onClick={handleViewAddLine} disabled={viewModal.saving}>
                        Добавить строку
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={handleOpenLoadAnswers} disabled={viewModal.saving}>
                        Подгрузить ответы
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleDownloadView}
                        disabled={!canDownloadView}
                        title={!canDownloadView ? 'Заполните все поля и корректный result во всех строках' : viewDirty ? 'Сначала сохранится текущий файл, затем начнётся скачивание' : 'Скачать JSON файл'}
                      >
                        Скачать
                      </button>
                    </div>
                  </div>
                  <div className="datasets-view-table-wrap">
                    <table className="datasets-view-table">
                      <thead>
                        <tr>
                          <th className="dataset-view-cell-question">Вопрос</th>
                          <th className="dataset-view-cell-correct">Правильный ответ</th>
                          <th className="dataset-view-cell-user">Ответ пользователя</th>
                          <th className="dataset-view-cell-result">Результат</th>
                          <th className="dataset-view-cell-actions">Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewModal.lines.map((line, index) => renderViewTableRow(line, index, viewModal.type))}
                      </tbody>
                    </table>
                  </div>
                  {viewModal.lines.length === 0 && !viewModal.loading && !viewModal.parseError && (
                    <p className="datasets-empty">Нет строк. Нажмите «Добавить строку».</p>
                  )}
                  {viewDirty && canSaveView && <div className="form-hint">Есть несохранённые изменения.</div>}
                  {viewModal.error && <div className="error-message">{viewModal.error}</div>}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseView} disabled={viewModal.saving}>
                Закрыть
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveView}
                disabled={!canSaveView}
                title={!viewDirty ? 'Нет изменений' : !validateAllLines(viewModal.lines, viewModal.type) ? 'Заполните все поля во всех строках и корректный result' : ''}
              >
                {viewModal.saving ? 'Сохранение…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load user answers modal */}
      {loadAnswersModalOpen && (
        <div className="modal-overlay" onClick={handleCloseLoadAnswers}>
          <div className="modal-content datasets-load-answers-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Подгрузить ответы пользователей</h3>
              <button type="button" className="modal-close" onClick={handleCloseLoadAnswers} aria-label="Закрыть" disabled={loadAnswersLoading}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {viewModal.type === DATASET_TYPE_IS_CORRECT && (
                <div className="form-group">
                  <span className="form-label">Подгружать ответы с result</span>
                  <div className="datasets-load-checkboxes">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={loadIsCorrectFilter.includes(0)}
                        onChange={() => toggleLoadIsCorrect(0)}
                        disabled={loadAnswersLoading}
                      />
                      <span>0 (неправильный)</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={loadIsCorrectFilter.includes(1)}
                        onChange={() => toggleLoadIsCorrect(1)}
                        disabled={loadAnswersLoading}
                      />
                      <span>1 (правильный)</span>
                    </label>
                  </div>
                </div>
              )}
              <div className="form-group">
                <label htmlFor="load-limit">Количество последних записей (по id ответа)</label>
                <input
                  id="load-limit"
                  type="number"
                  min={1}
                  max={10000}
                  className="form-control"
                  value={loadLimit}
                  onChange={e => setLoadLimit(Math.max(1, Math.min(10000, parseInt(e.target.value, 10) || 1)))}
                  disabled={loadAnswersLoading}
                />
              </div>
              {loadAnswersError && <div className="error-message">{loadAnswersError}</div>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseLoadAnswers} disabled={loadAnswersLoading}>
                Отмена
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmitLoadAnswers}
                disabled={!canSubmitLoadAnswers || loadAnswersLoading}
              >
                {loadAnswersLoading ? 'Подгрузка…' : 'Подгрузить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create modal — structured lines */}
      {createModalOpen && (
        <div className="modal-overlay" onClick={handleCloseCreate}>
          <div className="modal-content datasets-create-modal datasets-create-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Создать датасет</h3>
              <button type="button" className="modal-close" onClick={handleCloseCreate} aria-label="Закрыть">
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="datasets-create-hint">
                Выберите тип JSON: is-correct (result 0 или 1) или result (result от 1 до 10). Имя файла будет с префиксом типа.
              </p>
              <div className="form-group">
                <label htmlFor="dataset-type">Тип JSON</label>
                <select
                  id="dataset-type"
                  className="form-control"
                  value={createType}
                  onChange={e => setCreateType(e.target.value)}
                >
                  {DATASET_TYPES.map(t => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="dataset-name">Название (к имени файла добавится префикс типа)</label>
                <input
                  id="dataset-name"
                  type="text"
                  className="form-control"
                  value={createName}
                  onChange={e => setCreateName(e.target.value)}
                  placeholder="Например: answers-2024-01"
                />
                {createFullName && (
                  <div className="form-hint">Имя файла: {createFullName}</div>
                )}
              </div>
              <div className="datasets-line-toolbar">
                <button type="button" className="btn btn-primary" onClick={handleCreateAddLine}>
                  Добавить строку
                </button>
              </div>
              <div className="dataset-lines-list">
                {createLines.map((line, index) =>
                  renderLineRow(
                    line,
                    index,
                    createEditingIndex === index,
                    setCreateEditingIndex,
                    () => setCreateEditingIndex(null),
                    handleCreateUpdateLine,
                    handleCreateDeleteLine,
                    () => setCreateEditingIndex(null),
                    createType
                  )
                )}
              </div>
              {createLines.length === 0 && (
                <p className="datasets-empty">Добавьте хотя бы одну строку.</p>
              )}
              {!validateAllLines(createLines, createType) && createLines.length > 0 && (
                <div className="form-hint error">
                  Заполните во всех строках: question, correct_answer, user_message (не пусто) и result ({createType === DATASET_TYPE_RESULT ? '1–10' : '0 или 1'}).
                </div>
              )}
              {createError && <div className="error-message">{createError}</div>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseCreate} disabled={createSaving}>
                Отмена
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveDataset}
                disabled={!canSaveCreate}
                title={!validateAllLines(createLines, createType) ? 'Заполните все поля во всех строках согласно типу' : ''}
              >
                {createSaving ? 'Сохранение…' : 'Сохранить датасет'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatasetsSubTab;
